import type {
  BaseServiceOptions,
  Pagination,
  TBaseCatalogs,
  TRecordAction,
  TRequestCatalog,
} from '@/core/types'
import {
  type TProject,
  moduleServiceHandlers,
  type TProjects,
  serviceConfig,
  catalogServiceHandlers,
  TBundles,
  TCoilsReports,
} from '../../..'
import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'
import { useCatalogStore } from '@/core/store/catalogs'
import { useCoilsStore } from '../../store/coils'
import { TCustomer } from '@/modules/customers'

const { model } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, filters, handlers, enableQuery, modelId } = options
  const store = useCatalogStore()
  const coilsStore = useCoilsStore()
  const { errorHttpNotification } = useNotification()
  const { customersServiceHandlers } = catalogServiceHandlers
  const { customers } = serviceConfig.catalogs

  const serviceHandlers = useServiceHandlers<TProject>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const getTotalPercentage = (total: number, quantity: number) => {
    if (!total) return 0

    const result = (quantity / total) * 100

    return result >= 100 ? 100 : result
  }

  const deleteRecord = useMutation<void, AxiosError, TProject>(
    (payload) => moduleServiceHandlers.deleteRecord(payload),
    {
      onSuccess: (_, warehouse) => serviceHandlers.deleteRecord(warehouse),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TProject, AxiosError, TProject>(
    (payload: TProject) =>
      moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TProject, AxiosError, TProject>(
    (payload: TProject) => moduleServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const actionRecord = useMutation<TProject, AxiosError, TRecordAction>(
    (payload: TRecordAction) => moduleServiceHandlers.actionRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TProjects, AxiosError>({
    queryKey: [model, currentPage, filters?.loadRecords],
    queryFn: () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecords,
    onError: (error) => errorHttpNotification(error),
    select: (data) => ({
      ...data,
      data: data.data.map((record) => ({
        ...record,
        total_production_percentage: getTotalPercentage(
          record.total_weight_ton ?? 0,
          record.total_production ?? 0,
        ),
      })),
    }),
    onSuccess: (data) => {
      if (data?.meta) {
        handlers?.onDataChange?.(data?.meta ?? defaultPagination)
      }
    },
  })

  const loadRecord = useQuery<TProject, AxiosError>({
    queryKey: [model, modelId],
    queryFn: ({ queryKey }) => {
      return moduleServiceHandlers.loadRecord({
        id: Number(queryKey[1]),
      })
    },
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecord,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data.id) {
        handlers?.onLoadRecord?.(data)
      }
      coilsStore.setCoils(data.coils ?? [])
    },
    select: (data) => ({
      ...data,
      total_production_percentage: getTotalPercentage(
        data.total_weight_ton ?? 0,
        data.total_production ?? 0,
      ),
    }),
  })

  const loadHeaders = useQuery<TProject, AxiosError>({
    queryKey: ['header', model, modelId],
    queryFn: ({ queryKey }) =>
      moduleServiceHandlers.loadHeaders({
        id: Number(queryKey[2]),
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecord,
    onError: (error) => errorHttpNotification(error),
    select: (data) => ({
      ...data,
      total_production_percentage: getTotalPercentage(
        data.total_weight_ton ?? 0,
        data.total_production ?? 0,
      ),
    }),
  })

  const loadBundlesFabricated = useQuery<TBundles, AxiosError>({
    queryKey: [model, currentPage, filters?.loadBundlesFabricated, modelId],
    queryFn: () =>
      moduleServiceHandlers.loadBundlesFabricated({
        dynamicFilters: filters?.loadBundlesFabricated,
        page: currentPage,
        modelId,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadBundlesFabricated,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data?.meta) {
        handlers?.onDataChange?.(data?.meta ?? defaultPagination)
      }
    },
  })

  const loadUsedCoils = useQuery<TCoilsReports, AxiosError>({
    queryKey: [modelId],
    queryFn: () => {
      return moduleServiceHandlers.loadUsedCoils({
        modelId: toValue(modelId),
      })
    },

    onError: (error) => errorHttpNotification(error),
    enabled: enableQuery?.loadUsedCoils,
    refetchOnWindowFocus: false,
  })

  const requestCatalog = useMutation<
    TBaseCatalogs,
    AxiosError,
    TRequestCatalog
  >(
    (payload: Record<any, any>) =>
      catalogServiceHandlers.categoriesCatalog.requestCatalog(payload),
    {
      onSuccess: (payload) => {
        store.setCatalogs(payload)
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const customersCatalog = useQuery<TCustomer[], AxiosError>({
    queryKey: [customers, filters?.customersCatalog],
    queryFn: () =>
      customersServiceHandlers.loadCatalog({
        dynamicFilters: filters?.customersCatalog,
      }),
    onError: (error) => errorHttpNotification(error),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.customersCatalog,
  })

  return {
    loadRecords,
    loadRecord,
    addRecord,
    editRecord,
    deleteRecord,
    actionRecord,
    requestCatalog,
    customersCatalog,
    loadBundlesFabricated,
    loadUsedCoils,
    loadHeaders,
  }
}
